import { useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { ColumnDef } from '@tanstack/react-table';
import i18n from 'i18next';

import { Tag } from '@/components/generic';
import { DEFAULT_DEVICE_PORT } from '@/constants';
import { LinkIcon } from '@/resources';
import { Device, Event, TableData } from '@/types';

import { getEventStatusLabel } from './EventsHelper';

/**
 * Origin of a device's own web server, e.g. http://192.168.1.5:5012 - the base for both the
 * controller link and the per-screen player links.
 *
 * The port is whatever the device reported through its heartbeat, because it is not fixed: the
 * installer moves the player off 5011 when something else on that machine already holds it. A
 * device that has never reported one is either running software older than 2.0.15 or has not
 * checked in yet, and 5011 is the right guess for both.
 *
 * Returns null when the device has no known IP, so callers can render the same "not configured"
 * state they already show instead of a URL with an empty host.
 */
export const getDeviceBaseUrl = (
  localIP?: string | null,
  serverPort?: number | null
): string | null => (localIP ? `http://${localIP}:${serverPort ?? DEFAULT_DEVICE_PORT}` : null);

export const OrgPopup = ({
  orgs,
  anchorRect,
  onClose
}: {
  orgs: Array<{ id?: number; name: string }>;
  anchorRect: DOMRect | null;
  onClose: () => void;
}) => {
  if (!anchorRect) {
    return null;
  }
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: anchorRect.bottom + 4,
        left: anchorRect.left,
        zIndex: 9999
      }}
      className='min-w-[140px] rounded-lg bg-primary-gray p-2 text-xs text-secondary-steel-blue shadow-lg'
      onMouseLeave={onClose}>
      {orgs.map((org, idx) => (
        <div key={org.id ?? idx} className='px-2 py-1'>
          {`${idx + 1}. ${org.name}`}
        </div>
      ))}
    </div>,
    document.body
  );
};

export const getDeviceColumns: () => ColumnDef<TableData>[] = () => [
  {
    id: 'name',
    header: i18n.t('hardware', { ns: 'common' }),
    accessorKey: 'name',
    cell: (props) => {
      return (
        <div className='relative flex w-[280px] flex-row items-center md:w-[450px]'>
          <div className='flex max-w-[230px] flex-col md:max-w-[400px]'>
            <p className='font-medium'>{(props.row.original as Device)?.name}</p>
            <p className='text-xs opacity-80'>{(props.row.original as Device)?.description}</p>
          </div>
          <div className='ml-2 md:hidden'>
            <Tag value={(props.row.original as Device)?.status as string} />
          </div>
        </div>
      );
    }
  },
  {
    id: 'organizations',
    header: i18n.t('organizations', { ns: 'common' }),
    accessorKey: 'organizations',
    cell: (props) => {
      const orgs = (props.row.original as Device)?.organizations ?? [];
      const [showPopup, setShowPopup] = useState(false);
      const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
      const extraRef = useRef<HTMLDivElement>(null);
      if (!orgs.length) {
        return null;
      }
      const firstOrg = orgs[0]?.name;
      const extraCount = orgs.length - 1;
      const handleMouseEnter = () => {
        if (extraRef.current) {
          setAnchorRect(extraRef.current.getBoundingClientRect());
        }
        setShowPopup(true);
      };
      const handleMouseLeave = () => {
        setShowPopup(false);
      };
      return (
        <div className='relative flex items-center'>
          <span className='text-sm'>{firstOrg}</span>
          {extraCount > 0 && (
            <div
              ref={extraRef}
              className='ml-2 cursor-pointer rounded-lg bg-primary-gray px-2 text-xs text-primary-blue'
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              style={{ position: 'relative', zIndex: 1 }}>
              +{extraCount}
              {showPopup && (
                <OrgPopup orgs={orgs.slice(1)} anchorRect={anchorRect} onClose={handleMouseLeave} />
              )}
            </div>
          )}
        </div>
      );
    }
  },
  {
    id: 'status',
    header: i18n.t('status', { ns: 'common' }),
    accessorKey: 'status',
    cell: (props) => (
      <div className='hidden md:flex'>
        <Tag value={props.getValue() as string} />
      </div>
    )
  }
];

const LinkCopiedPopup = ({ anchorRect }: { anchorRect: DOMRect | null }) => {
  if (!anchorRect) {
    return null;
  }
  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: anchorRect.top - 32,
        left: anchorRect.left + anchorRect.width / 2,
        transform: 'translateX(-50%)',
        zIndex: 9999
      }}
      className='whitespace-nowrap rounded-lg bg-primary-black px-2 py-1 text-xs text-primary-white'>
      {i18n.t('linkCopied', { ns: 'common' }) as string}
    </div>,
    document.body
  );
};

const PlayerLinkCell = ({ url }: { url: string | null | undefined }) => {
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const iconRef = useRef<HTMLDivElement>(null);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url ?? '');
    if (iconRef.current) {
      setAnchorRect(iconRef.current.getBoundingClientRect());
    }
    setTimeout(() => setAnchorRect(null), 2000);
  };

  if (!url) {
    return (
      <p className='text-xs italic text-secondary-gray'>
        {i18n.t('noLocalIpConfigured', { ns: 'common' }) as string}
      </p>
    );
  }

  return (
    <div className='relative flex items-center gap-2'>
      <p className='truncate font-mono text-xs text-primary-blue underline'>{url}</p>
      <div ref={iconRef} className='relative'>
        <LinkIcon
          className='w-4 shrink-0 cursor-pointer text-secondary-gray hover:text-primary-blue'
          onClick={handleCopy}
        />
        {anchorRect && <LinkCopiedPopup anchorRect={anchorRect} />}
      </div>
    </div>
  );
};

export const getDeviceScreensColumns: () => ColumnDef<TableData>[] = () => [
  {
    id: 'name',
    header: i18n.t('name', { ns: 'common' }),
    accessorKey: 'name',
    cell: (props) => <div className='font-medium'>{props.getValue() as string}</div>
  },
  {
    id: 'playerLink',
    header: i18n.t('playerLink', { ns: 'common' }),
    accessorKey: 'playerLink',
    cell: (props) => <PlayerLinkCell url={props.getValue() as string | null | undefined} />
  }
];

export const getDeviceEventsColumns: () => ColumnDef<TableData>[] = () => [
  {
    id: 'name',
    header: i18n.t('event', { ns: 'common' }),
    accessorKey: 'name',
    cell: (props) => <div className='font-medium md:w-[600px]'>{props.getValue() as string}</div>
  },
  {
    id: 'organization',
    accessorKey: 'organization',
    header: i18n.t('organization', { ns: 'common' }),
    cell: (props) => (
      <div className='flex justify-end md:justify-start'>{props.getValue() as string}</div>
    )
  },
  {
    id: 'date',
    header: i18n.t('dateTime', { ns: 'common' }),
    accessorKey: 'date'
  },
  {
    id: 'status',
    header: i18n.t('status', { ns: 'common' }),
    accessorKey: 'status',
    cell: (props) => (
      <Tag
        value={getEventStatusLabel(props.getValue() as string)}
        statusDescription={(props.row.original as Event)?.statusDescription}
      />
    )
  }
];
